import { SpriteIcon } from "@components/core/icons/SpriteIcon";
import { FALLBACK_WAVEFORM } from "@lib/constants/player";
import { useWindowContext } from "@lib/context/window";
import logger from "@lib/logger";
import Color from "color";
import React, { useEffect, useRef } from "react";
import { WaveformContainer, Wrapper, ZoomControls } from "./Waveform.style";

export interface Props {
	showZoomControls: boolean;
	zoom: boolean;
	onToggleZoom: (setZoom: boolean) => void;
	loading: boolean;
	image: string;
	length: number;
	sampleStart: number;
	sampleEnd: number;
	current: () => number;
	onSeek?: (point: number) => void;
	redraw?: boolean;
}

const Waveform: React.FC<Props> = ({
	showZoomControls,
	zoom,
	onToggleZoom,
	loading,
	image,
	length,
	sampleStart,
	sampleEnd,
	current,
	onSeek,
	redraw,
}): React.ReactElement => {
	const COLOR_MARKER = Color("rgb(255, 255, 255)");
	const COLOR_ACCENT = Color("rgb(57, 193, 222)");
	const COLOR_TRANSPARENT = Color("#0000ffff");

	const COLOR_TRACK_PLAYED = COLOR_ACCENT.lighten(0.8);
	const COLOR_TRACK_UNPLAYED = COLOR_ACCENT;
	const COLOR_TRACK_AVAILABLE = COLOR_ACCENT.darken(0.8);

	const makeGradient = (
		{ context, color, height }: { context: CanvasRenderingContext2D; color: Color; height: number },
	): CanvasGradient => {
		const gradient = context.createLinearGradient(0, 0, 0, height);
		gradient.addColorStop(0, color.darken(0.25).rgb().toString());
		gradient.addColorStop(0.33, color.rgb().toString());
		gradient.addColorStop(0.5, color.lighten(0.25).rgb().toString());
		gradient.addColorStop(0.66, color.rgb().toString());
		gradient.addColorStop(1, color.darken(0.25).rgb().toString());

		return gradient;
	};

	const waveformRef = useRef<HTMLDivElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const rafRef = useRef<number>();
	const seekPositionRef = useRef<number>();
	const { width } = useWindowContext();

	const drawWaveform = (
		{ ctx, img, width, height, ss, se, tl, ct, zoom, seekX }: { ctx: CanvasRenderingContext2D; img: HTMLImageElement; width: number; height: number; ss: number; se: number; tl: number; ct: number; zoom: boolean; seekX?: number },
	): void => {
		// resize canvas to proper size
		ctx.canvas.height = height;
		ctx.canvas.width = width;

		// clear canvas context
		ctx.clearRect(0, 0, width, height);

		const sampleStartX = zoom ? 0 : (ss / tl) * width;
		const sampleLengthX = zoom ? width : ((se - ss) / tl) * width;
		const currentTimeX = zoom ?
				((ct - ss) / (se - ss)) * width :
				((ct - ss) / tl) * width;

		const showCT = currentTimeX > 0;

		// draw waveform
		ctx.globalCompositeOperation = "destination-atop";
		ctx.fillStyle = makeGradient({ context: ctx, color: COLOR_TRACK_AVAILABLE, height });
		ctx.fillRect(0, 0, width, height);
		ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, width, height);

		// Clear a space for the gradient
		ctx.clearRect(sampleStartX, 0, sampleLengthX, height);

		// Draw available sample
		ctx.globalCompositeOperation = "source-over";
		ctx.drawImage(
			img,
			(ss / tl) * img.width,
			0,
			((se - ss) / tl) * img.width,
			img.height,
			sampleStartX,
			0,
			sampleLengthX,
			height,
		);

		// Draw played sample
		ctx.globalCompositeOperation = "source-atop";
		ctx.fillStyle = makeGradient({ context: ctx, color: COLOR_TRACK_PLAYED, height });
		ctx.fillRect(sampleStartX, 0, currentTimeX, height);

		// Draw remaining track
		ctx.fillStyle = makeGradient({ context: ctx, color: COLOR_TRACK_UNPLAYED, height });
		ctx.fillRect(
			sampleStartX + currentTimeX,
			0,
			sampleLengthX - currentTimeX,
			height,
		);

		// Fade out edges
		ctx.globalCompositeOperation = "destination-in";
		const gradient = ctx.createLinearGradient(0, 0, 0, height);
		gradient.addColorStop(0, COLOR_TRANSPARENT.toString());
		gradient.addColorStop(0.1, COLOR_MARKER.toString());
		gradient.addColorStop(0.9, COLOR_MARKER.toString());
		gradient.addColorStop(1, COLOR_TRANSPARENT.toString());
		ctx.fillStyle = gradient;
		ctx.fillRect(0, 0, width, height);

		if (showCT) {
			// Draw current time marker
			ctx.globalCompositeOperation = "source-over";
			ctx.beginPath();
			ctx.strokeStyle = COLOR_MARKER.toString();
			ctx.lineWidth = 2;
			ctx.moveTo(sampleStartX + currentTimeX, 0);
			ctx.lineTo(sampleStartX + currentTimeX, height);
			ctx.stroke();
		}
		// Draw sample start marker
		if (ss !== 0) {
			ctx.globalCompositeOperation = "source-over";
			ctx.beginPath();
			ctx.strokeStyle = COLOR_MARKER.toString();
			ctx.lineWidth = 2;
			ctx.moveTo(sampleStartX, 0);
			ctx.lineTo(sampleStartX, height);
			ctx.stroke();
		}

		// Draw sample end marker
		if (se !== tl) {
			ctx.globalCompositeOperation = "source-over";
			ctx.beginPath();
			ctx.strokeStyle = COLOR_MARKER.toString();
			ctx.lineWidth = 2;
			ctx.moveTo(sampleStartX + sampleLengthX, 0);
			ctx.lineTo(sampleStartX + sampleLengthX, height);
			ctx.stroke();
		}

		if (seekX) {
			const rect = canvasRef.current?.getBoundingClientRect();
			if (rect) {
				const dX = seekX - rect.left;
				ctx.globalCompositeOperation = "source-over";
				ctx.beginPath();
				ctx.strokeStyle = COLOR_MARKER.toString();
				ctx.lineWidth = 1;
				ctx.moveTo(dX, 0);
				ctx.lineTo(dX, height);
				ctx.stroke();
			}
		}
	};

	const seek = (e: any) => {
		const rect = canvasRef?.current?.getBoundingClientRect();
		if (!rect) return;
		const p = (e.clientX - rect?.left) / rect?.width;

		const t = zoom ? p * (sampleEnd - sampleStart) + sampleStart : p * length;

		onSeek?.(t);
	};

	const getMousePosition = (e: MouseEvent) => {
		seekPositionRef.current = e.clientX;
	};

	const seekOn = () => {
		if (onSeek) {
			window.addEventListener("mousemove", getMousePosition);
		}
	};

	const seekOff = () => {
		if (onSeek) {
			window.removeEventListener("mousemove", getMousePosition);
			seekPositionRef.current = 0;
		}
	};

	useEffect(() => {
		const width = waveformRef?.current?.clientWidth;
		if (width === undefined) return;
		const height = waveformRef?.current?.clientHeight;
		if (height === undefined) return;

		const ctx = canvasRef.current?.getContext("2d");
		if (!ctx) return;

		// cerate image object
		const img = new Image();
		img.src = image;
		img.onload = loading ?
				() => { } :
				() => {
				// draw waveform when image is loaded
					rafRef.current = window.requestAnimationFrame(function draw() {
						drawWaveform(
							{ ctx, img, width, height, ss: sampleStart, se: sampleEnd, tl: length, ct: current(), zoom, seekX: seekPositionRef.current },
						);
						// redraw
						if (redraw) requestAnimationFrame(draw);
					});
				};

		img.onerror = () => {
			logger.error({ waveformSrc: img.src }, "Waveform image load error");

			const fallbackImage = new Image();
			fallbackImage.src = FALLBACK_WAVEFORM;
			// draw waveform when image is loaded
			rafRef.current = window.requestAnimationFrame(function draw() {
				drawWaveform(
					{ ctx, img: fallbackImage, width, height, ss: sampleStart, se: sampleEnd, tl: length, ct: current(), zoom, seekX: seekPositionRef.current },
				);
				// redraw
				if (redraw) requestAnimationFrame(draw);
			});
		};

		return () => {
			if (rafRef.current) {
				window.cancelAnimationFrame(rafRef.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		length,
		sampleStart,
		sampleEnd,
		current,
		image,
		seekPositionRef,
		width,
		loading,
		zoom,
		redraw,
	]);

	return (
		<Wrapper className="waveform-wrapper">
			<WaveformContainer
				ref={waveformRef}
				className={loading ? "loading" : undefined}
			>
				<canvas
					ref={canvasRef}
					onMouseOver={seekOn}
					onMouseOut={seekOff}
					style={{ width: "100%", cursor: "pointer", height: "50px" }}
					onClick={seek}
				/>
			</WaveformContainer>
			{showZoomControls && (
				<ZoomControls>
					{zoom ?
							(
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										onToggleZoom(!zoom);
									}}
								>
									<SpriteIcon id="waveform-zoom-out" width="25" height="25" />
								</a>
							) :
							(
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										onToggleZoom(!zoom);
									}}
								>
									<SpriteIcon id="waveform-zoom-in" width="25" height="25" />
								</a>
							)}
				</ZoomControls>
			)}
		</Wrapper>
	);
};

export default Waveform;
